// ============= i18n SYSTEM (Inclusion+AI v3.8) =============
// Lightweight bilingual (ar/en) context with localStorage persistence + RTL/LTR auto-switch.
// Usage: const { t, lang, setLang, dir } = useT();  -> t("login.title")
//
// Adding more strings: just extend MESSAGES below — missing keys gracefully fall back to the key itself.

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Languages } from "lucide-react";

const STORAGE_KEY = "inclusion_ai_lang_v1";
const DEFAULT_LANG = "ar";

// === MESSAGES ===
export const MESSAGES = {
  ar: {
    "brand.name": "Inclusion+AI",
    "brand.tagline": "منصة دمج تعليمي ذكية للطلاب من ذوي الاحتياجات الخاصة",

    // Common
    "common.loading": "...",
    "common.save": "حفظ",
    "common.cancel": "إلغاء",
    "common.delete": "حذف",
    "common.edit": "تعديل",
    "common.search": "بحث",
    "common.confirm": "تأكيد",
    "common.back": "رجوع",
    "common.next": "التالي",
    "common.email": "البريد الإلكتروني",
    "common.password": "كلمة المرور",
    "common.name": "الاسم",
    "common.error": "حدث خطأ",
    "common.success": "تم بنجاح",
    "common.logout": "تسجيل خروج",
    "common.language": "اللغة",

    // Login
    "login.title": "تسجيل الدخول",
    "login.submit": "دخول",
    "login.no_account": "ليس لديك حساب؟",
    "login.register_now": "سجّل الآن",
    "login.parent_access": "ولي أمر؟ ادخل بكود الطالب →",
    "login.demo_title": "حسابات تجريبية فورية",
    "login.demo_subtitle": "للتجربة بدون تسجيل",
    "login.demo_or_real": "أو سجّل بحسابك",
    "login.demo_creds_label": "بيانات الدخول:",
    "login.demo_head": "دخول كـ رئيس قسم الدمج",
    "login.demo_head_desc": "تجربة لوحة القيادة + الحالات + التقارير",
    "login.demo_specialist": "دخول كـ أخصائي / معالج",
    "login.demo_specialist_desc": "تجربة الحالات + الملاحظات المهنية + الأهداف",
    "login.demo_parent": "دخول كـ ولي أمر",
    "login.demo_parent_desc": "تجربة متابعة الطفل + الأنشطة المنزلية",
    "login.err_network": "تعذّر الاتصال بالخادم. تحقّق من الإنترنت أو حاول لاحقاً.",
    "login.err_invalid": "بيانات الدخول غير صحيحة",
    "login.err_server": "خطأ في الخادم — حاول مجدداً بعد قليل.",

    // Register
    "register.title": "إنشاء حساب جديد",
    "register.submit": "إنشاء الحساب",
    "register.have_account": "لديك حساب؟",
    "register.signin": "سجّل دخولك",
    "register.account_type": "نوع الحساب",
    "register.role_parent": "ولي أمر",
    "register.role_parent_desc": "متابعة طفلي، الفحص، الألعاب، المستشار الذكي",
    "register.role_head": "رئيس قسم الدمج",
    "register.role_head_desc": "إدارة حالات الدمج، فِرَق التدخل، تقييمات الفيديو",
    "register.role_specialist": "أخصائي / معالج",
    "register.role_specialist_desc": "ملاحظات مهنية، ربط بالحالات، أهداف العلاج",

    // Navigation
    "nav.dashboard": "لوحة التحكم",
    "nav.children": "أبنائي",
    "nav.advisor": "المستشار الذكي",
    "nav.home_activities": "الأنشطة المنزلية",
    "nav.certificates": "شهادات الإنجاز",
    "nav.reminders": "التذكيرات",
    "nav.cases": "حالات الدمج",
    "nav.curriculum": "تكييف المناهج",
    "nav.screening": "فحص بالكاميرا",
    "nav.games": "ألعاب الدمج",
    "nav.analytics": "المقارنة والتحليلات",
    "nav.admin": "لوحة التحكم الإدارية",
    "nav.mode": "وضع",
    "nav.role_parent": "ولي أمر",
    "nav.role_head": "رئيس قسم الدمج",
    "nav.role_specialist": "أخصائي",

    // Admin Panel
    "admin.title": "لوحة التحكم الإدارية",
    "admin.subtitle": "إدارة شاملة للمنصة — مستخدمون، حالات، تقييمات، شهادات، وسجل النشاط",
    "admin.tab_overview": "نظرة عامة",
    "admin.tab_users": "المستخدمون",
    "admin.tab_children": "الأطفال",
    "admin.tab_cases": "الحالات",
    "admin.tab_evaluations": "التقييمات",
    "admin.tab_certificates": "الشهادات",
    "admin.tab_activity": "سجل النشاط",
    "admin.kpi_users": "إجمالي المستخدمون",
    "admin.kpi_parents": "أولياء الأمور",
    "admin.kpi_specialists": "الأخصائيون",
    "admin.kpi_heads": "رؤساء الدمج",
    "admin.kpi_children": "الأطفال",
    "admin.kpi_cases": "حالات الدمج",
    "admin.kpi_evaluations": "التقييمات",
    "admin.kpi_certificates": "الشهادات الصادرة",
    "admin.kpi_today": "تسجيلات اليوم",
    "admin.kpi_week": "تسجيلات هذا الأسبوع",
    "admin.col_name": "الاسم",
    "admin.col_email": "البريد",
    "admin.col_role": "الدور",
    "admin.col_account_type": "النوع",
    "admin.col_created": "تاريخ التسجيل",
    "admin.col_actions": "إجراءات",
    "admin.col_child": "الطفل",
    "admin.col_status": "الحالة",
    "admin.col_score": "النتيجة",
    "admin.col_date": "التاريخ",
    "admin.col_action": "النشاط",
    "admin.col_actor": "المستخدم",
    "admin.search_users": "ابحث بالاسم أو البريد...",
    "admin.confirm_delete_user": "هل أنت متأكد من حذف هذا المستخدم؟ سيتم حذف جميع بياناته نهائياً.",
    "admin.change_role": "تغيير الدور",
    "admin.empty_users": "لا يوجد مستخدمون مطابقون",
    "admin.empty_children": "لا يوجد أطفال مسجّلون بعد",
    "admin.empty_cases": "لا توجد حالات دمج بعد",
    "admin.empty_evaluations": "لا توجد تقييمات بعد",
    "admin.empty_certificates": "لا توجد شهادات صادرة بعد",
    "admin.empty_activity": "لا يوجد نشاط مسجّل",
    "admin.forbidden": "غير مصرّح. هذه الصفحة مخصّصة لرئيس قسم الدمج فقط.",

    // Dashboard (common)
    "dash.welcome": "أهلاً",
    "dash.loading_dashboard": "جارٍ تحميل لوحتك...",
    "dash.retry": "إعادة المحاولة",
    "dash.parent_mode": "وضع ولي الأمر",
    "dash.head_mode": "وضع رئيس قسم الدمج",
    "dash.specialist_mode": "وضع الأخصائي",
    "dash.parent_subtitle": "متابعة تقدم أبنائك · شهاداتهم · أنشطة منزلية · مستشارك الذكي — كل ما تحتاجه في مكان واحد.",
    "dash.specialist_subtitle": "عرض الحالات المُسنَدة إليك ضمن فريق التدخل",
    "dash.kpi_children": "أبنائي",
    "dash.kpi_children_sub_active": "الجميع نشط في الخطة",
    "dash.kpi_children_sub_empty": "أضف طفلك الأول",
    "dash.kpi_goals": "الأهداف النشطة",
    "dash.kpi_goals_total": "هدف إجمالاً",
    "dash.kpi_achieved": "إنجازات محققة",
    "dash.kpi_achievement_rate": "معدّل التحقيق",
    "dash.kpi_activities": "أنشطة منزلية",
    "dash.kpi_activities_sub": "مولّدة بالذكاء الاصطناعي",
    "dash.spec_kpi_my_cases": "حالاتي",
    "dash.spec_kpi_active": "حالات نشطة",
    "dash.spec_kpi_plans": "خطط مفعّلة",
    "dash.spec_latest_cases": "آخر الحالات",
    "dash.spec_view_all": "عرض الكل",
    "dash.spec_empty": "لا توجد حالات مُسنَدة بعد.",
    "dash.spec_open": "فتح",
    "dash.spec_years": "سنة",
    "dash.spec_specialists": "متخصص",

    // Notifications
    "notif.title": "الإشعارات",
    "notif.mark_all_read": "تعليم الكل مقروء",
    "notif.empty": "لا توجد إشعارات",

    // Time ago
    "time.now": "الآن",
    "time.minutes_ago": "قبل {n} د",
    "time.hours_ago": "قبل {n} س",
    "time.days_ago": "قبل {n} يوم",

    // Children
    "children.title_parent": "أبنائي",
    "children.title_other": "الأطفال",
    "children.add": "+ إضافة طفل",
    "children.empty": "لا يوجد أطفال بعد. أضف طفلك الأول للبدء.",
    "children.years": "سنة",
    "children.open_profile": "فتح الملف",
    "children.placeholder_name": "اسم الطفل",
    "children.placeholder_age": "العمر",
    "children.add_btn": "إضافة",
    "children.cancel": "إلغاء",

    // Parent access
    "access.title": "ادخل بكود الطالب",
    "access.subtitle": "إذا زوّدك المتخصص أو المدرسة بكود سري، أدخله للوصول لخطة طفلك.",
    "access.placeholder": "أدخل الكود (مثل: ABC123)",
    "access.submit": "دخول",
    "access.back_login": "← رجوع لتسجيل الدخول",
    "access.invalid_code": "الكود غير صحيح أو منتهي.",

    // Reminders
    "reminders.title": "التذكيرات",
    "reminders.add": "+ تذكير جديد",
    "reminders.empty": "لا توجد تذكيرات بعد.",
    "reminders.placeholder_title": "عنوان التذكير",
    "reminders.placeholder_when": "متى؟",
    "reminders.frequency": "التكرار",
    "reminders.freq_once": "مرة واحدة",
    "reminders.freq_daily": "يومياً",
    "reminders.freq_weekly": "أسبوعياً",
    "reminders.save": "حفظ",
    "reminders.delete": "حذف",

    // Home activities
    "ha.title": "الأنشطة المنزلية",
    "ha.subtitle": "أنشطة مخصّصة لطفلك يولّدها الذكاء الاصطناعي بناءً على خطته.",
    "ha.empty": "لا توجد أنشطة بعد.",
    "ha.steps_title": "خطواتك:",
    "ha.tip": "نصيحة:",
    "ha.copy_link": "نسخ الرابط",

    // Certificates gallery
    "cert.title": "شهادات إنجاز أبنائك",
    "cert.subtitle": "شهادات رسمية تَصدُر تلقائياً عند إنجاز كل هدف.",
    "cert.empty": "لا توجد شهادات بعد. عند إنجاز أهداف خطة الدمج، ستَصدر شهادات رسمية تلقائياً.",
    "cert.download": "تحميل PDF",
    "cert.print": "طباعة",
    "cert.share": "مشاركة",

    // Advisor
    "advisor.title": "المستشار الذكي",
    "advisor.subtitle": "اسأل خبير ذكاء اصطناعي مدرَّب على التربية والدمج.",
    "advisor.placeholder": "اكتب سؤالك...",
    "advisor.send": "إرسال",
    "advisor.new_chat": "محادثة جديدة",
    "advisor.empty": "ابدأ المحادثة بسؤالك الأول.",

    // Generic / status
    "status.active": "نشط",
    "status.achieved": "محقق",
    "status.in_progress": "قيد التنفيذ",
    "status.pending": "قيد الانتظار",
    "status.loading": "جاري التحميل...",
    "status.draft": "مسوّدة",
    "status.under_review": "قيد المراجعة",
    "status.closed": "مغلق",
    "status.deferred": "مؤجّل",

    // Advisor (extra)
    "advisor.subtitle_long": "مدعوم بـ GPT-5.2 · يجيب عن أسئلتك اليومية حول الدمج والتربية",
    "advisor.history": "السجل",
    "advisor.history_title": "المحادثات السابقة",
    "advisor.history_empty": "لا يوجد سجل بعد",
    "advisor.empty_session": "(فارغة)",
    "advisor.typing": "يكتب...",
    "advisor.placeholder_ask": "اسأل عن أي شيء...",
    "advisor.connection_error": "تعذر الاتصال. حاول مجدداً.",

    // Share modal
    "share.title_plan": "مشاركة خطة",
    "share.subtitle": "سيُنشأ رابط آمن للقراءة فقط يمكنك إرساله للمعالج المساعد.",
    "share.days_label": "صلاحية الرابط (أيام)",
    "share.generate": "توليد الرابط",
    "share.valid_until": "صالح حتى:",
    "share.copy": "نسخ",
    "share.copied": "تم النسخ",
    "share.close": "إغلاق",

    // Activity log labels
    "activity.case_created": "أُنشئت حالة",
    "activity.case_updated": "حُدّثت حالة",
    "activity.goal_added": "أُضيف هدف",
    "activity.goal_updated": "حُدّث هدف",
    "activity.goal_status": "تغيّرت حالة هدف",
    "activity.goal_removed": "حُذف هدف",
    "activity.goal_progress": "سُجّل تقدم لهدف",
    "activity.note_added": "أُضيفت ملاحظة",
    "activity.member_added": "أُضيف عضو",
    "activity.member_removed": "حُذف عضو",
    "activity.invite_sent": "أُرسلت دعوة",
    "activity.child_linked": "تم ربط طفل",
    "activity.child_unlinked": "فُكّ ربط طفل",
    "activity.evaluation_created": "أُنشئ تقييم فيديو",
    "activity.status_changed": "تغيّرت حالة الملف",

    // Pages titles
    "page.cases": "حالات الدمج",
    "page.curriculum": "تكييف المناهج",
    "page.curriculum_sub": "إدارة الأهداف والخطط بدورة حياة كاملة (مسوّدة → نشط → قيد المراجعة → محقق)",
    "page.analytics": "المقارنة والتحليلات",
    "page.screening": "فحص بالكاميرا",
    "page.games": "ألعاب الدمج التفاعلية",
    "page.head_command": "مركز قيادة قسم الدمج",
    "page.head_subtitle": "نظرة شاملة على القسم — حالات نشطة، فِرق التدخل، إنجاز الأهداف وتقييمات الفيديو.",
  },

  en: {
    "brand.name": "Inclusion+AI",
    "brand.tagline": "AI-powered inclusive education platform for learners with diverse support needs",

    "common.loading": "...",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.search": "Search",
    "common.confirm": "Confirm",
    "common.back": "Back",
    "common.next": "Next",
    "common.email": "Email",
    "common.password": "Password",
    "common.name": "Name",
    "common.error": "An error occurred",
    "common.success": "Success",
    "common.logout": "Sign out",
    "common.language": "Language",

    "login.title": "Sign in",
    "login.submit": "Sign in",
    "login.no_account": "Don't have an account?",
    "login.register_now": "Register now",
    "login.parent_access": "Parent? Enter your student code →",
    "login.demo_title": "Instant demo accounts",
    "login.demo_subtitle": "Try without registration",
    "login.demo_or_real": "Or sign in with your account",
    "login.demo_creds_label": "Credentials:",
    "login.demo_head": "Sign in as Inclusion Head",
    "login.demo_head_desc": "Try command dashboard + cases + reports",
    "login.demo_specialist": "Sign in as Specialist / Therapist",
    "login.demo_specialist_desc": "Try cases + clinical notes + goals",
    "login.demo_parent": "Sign in as Parent",
    "login.demo_parent_desc": "Try child tracking + home activities",
    "login.err_network": "Cannot reach the server. Check your internet or try later.",
    "login.err_invalid": "Invalid credentials",
    "login.err_server": "Server error — please try again shortly.",

    "register.title": "Create a new account",
    "register.submit": "Create account",
    "register.have_account": "Already have an account?",
    "register.signin": "Sign in",
    "register.account_type": "Account type",
    "register.role_parent": "Parent",
    "register.role_parent_desc": "Track my child, screening, games, AI advisor",
    "register.role_head": "Inclusion Head",
    "register.role_head_desc": "Manage cases, intervention teams, video evaluations",
    "register.role_specialist": "Specialist / Therapist",
    "register.role_specialist_desc": "Clinical notes, case linkage, therapy goals",

    "nav.dashboard": "Dashboard",
    "nav.children": "My children",
    "nav.advisor": "AI Advisor",
    "nav.home_activities": "Home activities",
    "nav.certificates": "Certificates",
    "nav.reminders": "Reminders",
    "nav.cases": "Inclusion cases",
    "nav.curriculum": "Curriculum adaptation",
    "nav.screening": "Camera screening",
    "nav.games": "Inclusive games",
    "nav.analytics": "Analytics",
    "nav.admin": "Admin panel",
    "nav.mode": "Mode",
    "nav.role_parent": "Parent",
    "nav.role_head": "Inclusion Head",
    "nav.role_specialist": "Specialist",

    "admin.title": "Admin Control Panel",
    "admin.subtitle": "Platform-wide management — users, cases, evaluations, certificates & activity log",
    "admin.tab_overview": "Overview",
    "admin.tab_users": "Users",
    "admin.tab_children": "Children",
    "admin.tab_cases": "Cases",
    "admin.tab_evaluations": "Evaluations",
    "admin.tab_certificates": "Certificates",
    "admin.tab_activity": "Activity log",
    "admin.kpi_users": "Total users",
    "admin.kpi_parents": "Parents",
    "admin.kpi_specialists": "Specialists",
    "admin.kpi_heads": "Inclusion heads",
    "admin.kpi_children": "Children",
    "admin.kpi_cases": "Inclusion cases",
    "admin.kpi_evaluations": "Evaluations",
    "admin.kpi_certificates": "Certificates issued",
    "admin.kpi_today": "Signups today",
    "admin.kpi_week": "Signups this week",
    "admin.col_name": "Name",
    "admin.col_email": "Email",
    "admin.col_role": "Role",
    "admin.col_account_type": "Type",
    "admin.col_created": "Joined",
    "admin.col_actions": "Actions",
    "admin.col_child": "Child",
    "admin.col_status": "Status",
    "admin.col_score": "Score",
    "admin.col_date": "Date",
    "admin.col_action": "Action",
    "admin.col_actor": "Actor",
    "admin.search_users": "Search by name or email...",
    "admin.confirm_delete_user": "Delete this user? All their data will be permanently removed.",
    "admin.change_role": "Change role",
    "admin.empty_users": "No matching users",
    "admin.empty_children": "No children registered yet",
    "admin.empty_cases": "No inclusion cases yet",
    "admin.empty_evaluations": "No evaluations yet",
    "admin.empty_certificates": "No certificates issued yet",
    "admin.empty_activity": "No activity recorded",
    "admin.forbidden": "Forbidden. This page is reserved for the Inclusion Head only.",

    "dash.welcome": "Welcome",
    "dash.loading_dashboard": "Loading your dashboard...",
    "dash.retry": "Retry",
    "dash.parent_mode": "Parent mode",
    "dash.head_mode": "Inclusion Head mode",
    "dash.specialist_mode": "Specialist mode",
    "dash.parent_subtitle": "Track your children's progress · certificates · home activities · your AI advisor — everything you need in one place.",
    "dash.specialist_subtitle": "View cases assigned to you within the intervention team",
    "dash.kpi_children": "My children",
    "dash.kpi_children_sub_active": "All active in plan",
    "dash.kpi_children_sub_empty": "Add your first child",
    "dash.kpi_goals": "Active goals",
    "dash.kpi_goals_total": "goals total",
    "dash.kpi_achieved": "Goals achieved",
    "dash.kpi_achievement_rate": "achievement rate",
    "dash.kpi_activities": "Home activities",
    "dash.kpi_activities_sub": "AI-generated",
    "dash.spec_kpi_my_cases": "My cases",
    "dash.spec_kpi_active": "Active cases",
    "dash.spec_kpi_plans": "Plans enabled",
    "dash.spec_latest_cases": "Latest cases",
    "dash.spec_view_all": "View all",
    "dash.spec_empty": "No assigned cases yet.",
    "dash.spec_open": "Open",
    "dash.spec_years": "yrs",
    "dash.spec_specialists": "specialist(s)",

    "notif.title": "Notifications",
    "notif.mark_all_read": "Mark all read",
    "notif.empty": "No notifications",

    "time.now": "now",
    "time.minutes_ago": "{n}m ago",
    "time.hours_ago": "{n}h ago",
    "time.days_ago": "{n}d ago",

    "children.title_parent": "My children",
    "children.title_other": "Children",
    "children.add": "+ Add child",
    "children.empty": "No children yet. Add your first child to get started.",
    "children.years": "yrs",
    "children.open_profile": "Open profile",
    "children.placeholder_name": "Child's name",
    "children.placeholder_age": "Age",
    "children.add_btn": "Add",
    "children.cancel": "Cancel",

    "access.title": "Enter your student code",
    "access.subtitle": "If a specialist or school gave you a secret code, enter it to access your child's plan.",
    "access.placeholder": "Enter the code (e.g. ABC123)",
    "access.submit": "Enter",
    "access.back_login": "← Back to sign-in",
    "access.invalid_code": "Invalid or expired code.",

    "reminders.title": "Reminders",
    "reminders.add": "+ New reminder",
    "reminders.empty": "No reminders yet.",
    "reminders.placeholder_title": "Reminder title",
    "reminders.placeholder_when": "When?",
    "reminders.frequency": "Frequency",
    "reminders.freq_once": "Once",
    "reminders.freq_daily": "Daily",
    "reminders.freq_weekly": "Weekly",
    "reminders.save": "Save",
    "reminders.delete": "Delete",

    "ha.title": "Home activities",
    "ha.subtitle": "Tailored activities for your child, AI-generated from their plan.",
    "ha.empty": "No activities yet.",
    "ha.steps_title": "Your steps:",
    "ha.tip": "Tip:",
    "ha.copy_link": "Copy link",

    "cert.title": "Children's achievement certificates",
    "cert.subtitle": "Official certificates auto-issued upon each completed goal.",
    "cert.empty": "No certificates yet. When inclusion plan goals are achieved, official certificates are auto-issued.",
    "cert.download": "Download PDF",
    "cert.print": "Print",
    "cert.share": "Share",

    "advisor.title": "AI Advisor",
    "advisor.subtitle": "Ask an AI expert trained in education and inclusion.",
    "advisor.placeholder": "Type your question...",
    "advisor.send": "Send",
    "advisor.new_chat": "New chat",
    "advisor.empty": "Start the conversation with your first question.",

    "status.active": "Active",
    "status.achieved": "Achieved",
    "status.in_progress": "In progress",
    "status.pending": "Pending",
    "status.loading": "Loading...",
    "status.draft": "Draft",
    "status.under_review": "Under review",
    "status.closed": "Closed",
    "status.deferred": "Deferred",

    "advisor.subtitle_long": "Powered by GPT-5.2 · Answers your daily questions about inclusion and parenting",
    "advisor.history": "History",
    "advisor.history_title": "Past conversations",
    "advisor.history_empty": "No history yet",
    "advisor.empty_session": "(empty)",
    "advisor.typing": "typing...",
    "advisor.placeholder_ask": "Ask about anything...",
    "advisor.connection_error": "Connection error. Please try again.",

    "share.title_plan": "Share plan for",
    "share.subtitle": "A read-only secure link will be generated that you can send to a co-therapist.",
    "share.days_label": "Link validity (days)",
    "share.generate": "Generate link",
    "share.valid_until": "Valid until:",
    "share.copy": "Copy",
    "share.copied": "Copied",
    "share.close": "Close",

    "activity.case_created": "Case created",
    "activity.case_updated": "Case updated",
    "activity.goal_added": "Goal added",
    "activity.goal_updated": "Goal updated",
    "activity.goal_status": "Goal status changed",
    "activity.goal_removed": "Goal removed",
    "activity.goal_progress": "Goal progress logged",
    "activity.note_added": "Note added",
    "activity.member_added": "Member added",
    "activity.member_removed": "Member removed",
    "activity.invite_sent": "Invite sent",
    "activity.child_linked": "Child linked",
    "activity.child_unlinked": "Child unlinked",
    "activity.evaluation_created": "Video evaluation created",
    "activity.status_changed": "File status changed",

    "page.cases": "Inclusion cases",
    "page.curriculum": "Curriculum adaptation",
    "page.curriculum_sub": "Manage goals and plans through the full lifecycle (Draft → Active → Under review → Achieved)",
    "page.analytics": "Comparison & Analytics",
    "page.screening": "Camera screening",
    "page.games": "Inclusive games",
    "page.head_command": "Inclusion Department Command Center",
    "page.head_subtitle": "Department-wide overview — active cases, intervention teams, goal achievement and video evaluations.",
  },
};

const LangContext = createContext({
  lang: DEFAULT_LANG,
  setLang: () => {},
  t: (k) => k,
  dir: "rtl",
});

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG; } catch { return DEFAULT_LANG; }
  });

  // Sync <html dir> + lang attributes
  useEffect(() => {
    const dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.setAttribute("dir", dir);
    document.documentElement.setAttribute("lang", lang);
    try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* ignore */ }
  }, [lang]);

  const setLang = useCallback((l) => setLangState(l === "en" ? "en" : "ar"), []);

  const t = useCallback((key, fallback) => {
    const dict = MESSAGES[lang] || MESSAGES[DEFAULT_LANG];
    return dict[key] ?? fallback ?? key;
  }, [lang]);

  const value = { lang, setLang, t, dir: lang === "ar" ? "rtl" : "ltr" };
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useT = () => useContext(LangContext);

// === Language Toggle Button ===
export function LanguageToggle({ className = "", testid = "lang-toggle", compact = false }) {
  const { lang, setLang } = useT();
  const next = lang === "ar" ? "en" : "ar";
  const label = lang === "ar" ? "EN" : "عربي";
  return (
    <button
      type="button"
      onClick={() => setLang(next)}
      data-testid={testid}
      aria-label={`Switch language to ${next === "ar" ? "Arabic" : "English"}`}
      className={`inline-flex items-center gap-1.5 ${compact ? "px-2 py-1 min-w-[44px] justify-center" : "px-3 py-1.5"} rounded-full bg-white border border-[#E5E2DB] hover:border-[#2F5233] hover:bg-[#F9F8F6] text-xs font-semibold text-[#2F5233] transition min-h-[36px] ${className}`}
    >
      <Languages size={14} aria-hidden="true"/>
      <span>{label}</span>
    </button>
  );
}
